import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Toast } from 'primereact/toast';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { getObrasTabla, getGrabarObra, getEliminarObra } from '../../../../service/AffariService';
import LoaderTransparent from '../../../../components/ui/LoaderTransparent';



const ObrasDialog = ({ visible, onCancel, codigo, data, onChangeObrasLista }) => {
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nuevaDescripcion, setNuevaDescripcion] = useState('');
    const toast = useRef(null);
    const [obraEditando, setObraEditando] = useState(null);
    const [descripcionEditando, setDescripcionEditando] = useState('');


    const fetchObras = useCallback(() => {
        if (!codigo) return;
        setLoading(true);
        setObras([]);
        getObrasTabla(codigo)
            .then((resp) => {
                const lista = Array.isArray(resp) ? resp : resp?.obras || [];
                setObras(lista);
                if (onChangeObrasLista) {
                    onChangeObrasLista(lista);
                }
            })
            .catch((error) => {
                console.error('Error obteniendo propuestas del cliente:', error);
            })
            .finally(() => setLoading(false));
    }, [codigo, onChangeObrasLista]);
    
    useEffect(() => {
        if (!visible || !codigo) return;

        fetchObras();
    }, [visible, codigo, fetchObras]);


    const handleCrearObra = () => {
        if (!codigo || !nuevaDescripcion.trim()) return;

        setLoading(true);

        getGrabarObra({ codigo, descripcion: nuevaDescripcion.trim() })
            .then(() => getObrasTabla(codigo))
            .then((resp) => {
                const lista = Array.isArray(resp) ? resp : resp?.obras || [];
                setObras(lista);
                if (onChangeObrasLista) {
                    onChangeObrasLista(lista);
                }
                setNuevaDescripcion('');
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Obra',
                        detail: 'Obra creada con éxito',
                        life: 3000,
                    });
                }
            })
            .catch((error) => {
                console.error('Error grabando obra:', error);
            })
            .finally(() => setLoading(false));
    };

    const handleEliminarObra = (obra) => {
        if (!obra?.id) return;
        if (!window.confirm(`¿Eliminar la obra "${obra.descripcion}"?`)) return;

        setLoading(true);

        getEliminarObra(obra.id)
            .then(() => getObrasTabla(codigo))
            .then((resp) => {
                const lista = Array.isArray(resp) ? resp : resp?.obras || [];
                setObras(lista);
                if (onChangeObrasLista) {
                    onChangeObrasLista(lista);
                }
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Obra',
                        detail: 'Obra eliminada con éxito',
                        life: 3000,
                    });
                }
            })
            .catch((error) => {
                console.error('Error eliminando obra:', error);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Obra',
                        detail: 'No se pudo eliminar la obra',
                        life: 3000,
                    });
                }
            })
            .finally(() => setLoading(false));
    };

    const handleEditarObraStart = (obra) => {
        if (!obra?.id) return;
        setObraEditando(obra.id);
        setDescripcionEditando(obra.descripcion || '');
    };

    const handleEditarObraCancel = () => {
        setObraEditando(null);
        setDescripcionEditando('');
    };

    const handleEditarObraSave = () => {
        if (!obraEditando || !descripcionEditando.trim()) return;

        setLoading(true);
        const descripcionLimpia = descripcionEditando.trim();

        getGrabarObra({ id: obraEditando, codigo, descripcion: descripcionLimpia })
            .then(() => getObrasTabla(codigo))
            .then((resp) => {
                const lista = Array.isArray(resp) ? resp : resp?.obras || [];
                setObras(lista);
                if (onChangeObrasLista) {
                    onChangeObrasLista(lista);
                }
                setObraEditando(null);
                setDescripcionEditando('');
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Obra',
                        detail: 'Obra actualizada con éxito',
                        life: 3000,
                    });
                }
            })
            .catch((error) => {
                console.error('Error actualizando obra:', error);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Obra',
                        detail: 'No se pudo actualizar la obra',
                        life: 3000,
                    });
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <>
            <Toast ref={toast} position="top-right" baseZIndex={4000} appendTo={document.body} />
            <Dialog
            header={`Mantenedor de Obras`}
            visible={visible}
            className="p-fluid custom-dialog"
            modal
            onHide={onCancel}
            dismissableMask={true}
        >
            <div className="card mb-1">
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <span className="font-bold">Código Cliente: {codigo}</span>
                    </div>
                    <div className="col-12 md:col-6">
                        <span className="font-bold">RUT Cliente: {data?.cliente?.rut}</span>
                    </div>
                    <div className="col-12 md:col-6">
                        <span className="font-bold">Razón Social: {data?.cliente?.razonSocial}</span>
                    </div>
                </div>
            </div>
            <div className="card mb-3">
                <div className="grid">
                    <div className="mt-3 flex flex-column gap-2">
                        <span className="font-bold">Crear nueva obra</span>
                        <div className="flex gap-2">
                            <InputText
                                type="text"
                                placeholder="Descripción"
                                className="p-input-text p-input-sm"
                                value={nuevaDescripcion}
                                onChange={(e) => setNuevaDescripcion(e.target.value)}
                            />
                            <Button
                                label="Agregar"
                                className="p-button-success p-button-sm"
                                icon="pi pi-plus"
                                onClick={handleCrearObra}
                                disabled={!nuevaDescripcion.trim() || loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {loading && <LoaderTransparent coverParent overlayTint="transparent" />}
            <DataTable
                value={obras}
                tableStyle={{ minWidth: "50rem" }}
                size="small"
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50, 100]}
                dataKey="id"
                className="p-datatable-sm border-1 surface-border border-round"
                globalFilterFields={["cliente.razonSocial"]}
                stripedRows
                rowHover                              // resalta filas al hover
                showGridlines                         // muestra líneas de cuadrícula
                sortMode="multiple"                   // orden múltiple por columnas
                removableSort                         // permite limpiar orden
                resizableColumns                      // permitir redimensionar columnas
                columnResizeMode="fit"                // ajuste de columnas al redimensionar
                reorderableColumns                    // reordenar columnas arrastrando el header
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                currentPageReportTemplate="{first} - {last} de {totalRecords} obras"
                scrollable={true}
                emptyMessage={<div className="p-4 hover:font-bold">No hay ningún registro que mostrar</div>}
            >
                <Column
                    columnKey="acciones"
                    header="Acciones"
                    body={(rowData) => (
                        obraEditando === rowData.id ? (
                            <>
                                <Button
                                    icon="pi pi-check"
                                    className="p-button-rounded p-button-text p-button-success p-button-sm"
                                    onClick={handleEditarObraSave}
                                    disabled={loading || !descripcionEditando.trim()}
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                                    onClick={handleEditarObraCancel}
                                    disabled={loading}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    icon="pi pi-file"
                                    className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                                    onClick={() => handleEditarObraStart(rowData)}
                                    disabled={loading}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                                    onClick={() => handleEliminarObra(rowData)}
                                    disabled={loading}
                                />
                            </>
                        )
                    )}
                    style={{ width: '4.5rem' }}
                />
                <Column
                    columnKey="descripcion"
                    field="descripcion"
                    header="Descripción"
                    style={{ width: '12rem' }}
                    sortable
                    body={(rowData) => (
                        obraEditando === rowData.id ? (
                            <InputText
                                value={descripcionEditando}
                                onChange={(e) => setDescripcionEditando(e.target.value)}
                                className="p-inputtext-sm w-full"
                            />
                        ) : (
                            rowData.descripcion
                        )
                    )}
                />
            </DataTable>
        </Dialog>
        </>
    );
};

export default ObrasDialog;
